import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Routes, Route, Navigate } from "react-router-dom";
import CookieConsent from "./components/CookieConsent";
import BackToTop from "./components/BackToTop";
import ScrollToTop from "./components/ScrollToTop";

// Eager load home page for fast initial load
import Index from "./pages/Index";

// Lazy load all other routes to reduce initial bundle size
const Jobs = lazy(() => import("./pages/Jobs"));
const JobDetail = lazy(() => import("./pages/JobDetail"));
const CVRegistration = lazy(() => import("./pages/CVRegistration"));
const Contact = lazy(() => import("./pages/Contact"));
const About = lazy(() => import("./pages/About"));
const Hire = lazy(() => import("./pages/Hire"));
const FAQ = lazy(() => import("./pages/FAQ"));
const JobAlerts = lazy(() => import("./pages/JobAlerts"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Cookie = lazy(() => import("./pages/Cookie"));
const Terms = lazy(() => import("./pages/Terms"));
const ModernSlavery = lazy(() => import("./pages/ModernSlavery"));
const NotFound = lazy(() => import("./pages/NotFound"));
const StaffingCalculator = lazy(() => import("./pages/StaffingCalculator"));
const CleaningStaff = lazy(() => import("./pages/sectors/CleaningStaff"));
const PublicSector = lazy(() => import("./pages/sectors/PublicSector"));
const CorporateFM = lazy(() => import("./pages/sectors/CorporateFM"));
const Employers = lazy(() => import("./pages/Employers"));
const EqualityDiversityInclusion = lazy(() => import("./pages/EqualityDiversityInclusion"));
const HealthAndSafety = lazy(() => import("./pages/HealthAndSafety"));
const EnvironmentalPolicy = lazy(() => import("./pages/EnvironmentalPolicy"));
const PromptPaymentPolicy = lazy(() => import("./pages/PromptPaymentPolicy"));
const Locations = lazy(() => import("./pages/Locations"));
const Careers = lazy(() => import("./pages/Careers"));
const JobSearch = lazy(() => import("./pages/JobSearch"));

// Lazy load region pages
const LondonJobs = lazy(() => import("./pages/regions/LondonJobs"));
const BirminghamJobs = lazy(() => import("./pages/regions/BirminghamJobs"));
const WestMidlandsJobs = lazy(() => import("./pages/regions/WestMidlandsJobs"));
const KentJobs = lazy(() => import("./pages/regions/KentJobs"));
const ManchesterJobs = lazy(() => import("./pages/regions/ManchesterJobs"));
const YorkshireJobs = lazy(() => import("./pages/regions/YorkshireJobs"));
const SussexJobs = lazy(() => import("./pages/regions/SussexJobs"));
const BristolJobs = lazy(() => import("./pages/regions/BristolJobs"));
const WorcestershireJobs = lazy(() => import("./pages/regions/WorcestershireJobs"));
const DorsetJobs = lazy(() => import("./pages/regions/DorsetJobs"));
const OxfordshireJobs = lazy(() => import("./pages/regions/OxfordshireJobs"));
const MerseysideJobs = lazy(() => import("./pages/regions/MerseysideJobs"));
const CoventryJobs = lazy(() => import("./pages/regions/CoventryJobs"));
const DoncasterJobs = lazy(() => import("./pages/regions/DoncasterJobs"));
const HertfordshireJobs = lazy(() => import("./pages/regions/HertfordshireJobs"));
const CornwallJobs = lazy(() => import("./pages/regions/CornwallJobs"));
const SomersetJobs = lazy(() => import("./pages/regions/SomersetJobs"));
const BridgwaterJobs = lazy(() => import("./pages/regions/BridgwaterJobs"));
const CamborneJobs = lazy(() => import("./pages/regions/CamborneJobs"));
const ShropshireJobs = lazy(() => import("./pages/regions/ShropshireJobs"));
const HampshireJobs = lazy(() => import("./pages/regions/HampshireJobs"));
const StaffordshireJobs = lazy(() => import("./pages/regions/StaffordshireJobs"));
const BuckinghamshireJobs = lazy(() => import("./pages/regions/BuckinghamshireJobs"));
const IsleOfWightJobs = lazy(() => import("./pages/regions/IsleOfWightJobs"));
const SouthWalesJobs = lazy(() => import("./pages/regions/SouthWalesJobs"));
const SurreyJobs = lazy(() => import("./pages/regions/SurreyJobs"));

const App = () => (
  <TooltipProvider>
    <Toaster />
    <Sonner />
    <CookieConsent />
    <BackToTop />
    <ScrollToTop />
    <Suspense fallback={
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    }>
      <Routes>
        <Route path="/" element={<Index />} />
        <Route path="/new-home" element={<Navigate to="/" replace />} />
        <Route path="/jobs" element={<Jobs />} />
        <Route path="/locations" element={<Locations />} />
        <Route path="/careers" element={<Careers />} />
        <Route path="/jobs/search" element={<JobSearch />} />
        <Route path="/jobs/:jobId" element={<JobDetail />} />
        <Route path="/cv-registration" element={<CVRegistration />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/about" element={<About />} />
        <Route path="/hire" element={<Hire />} />
        <Route path="/employers" element={<Employers />} />
        <Route path="/staffing-calculator" element={<StaffingCalculator />} />
        <Route path="/cleaning-staff" element={<CleaningStaff />} />
        <Route path="/sectors/public-sector" element={<PublicSector />} />
        <Route path="/sectors/corporate-fm" element={<CorporateFM />} />
        <Route path="/sectors/government-justice" element={<Navigate to="/sectors/public-sector" replace />} />
        <Route path="/faq" element={<FAQ />} />
        <Route path="/job-alerts" element={<JobAlerts />} />
        <Route path="/unsubscribe" element={<Unsubscribe />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/cookie" element={<Cookie />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/modern-slavery" element={<ModernSlavery />} />
        <Route path="/equality-diversity-inclusion" element={<EqualityDiversityInclusion />} />
        <Route path="/health-and-safety" element={<HealthAndSafety />} />
        <Route path="/environmental-policy" element={<EnvironmentalPolicy />} />
        <Route path="/prompt-payment-policy" element={<PromptPaymentPolicy />} />
        {/* Region-specific job pages */}
        <Route path="/jobs/london" element={<LondonJobs />} />
        <Route path="/jobs/birmingham" element={<BirminghamJobs />} />
        <Route path="/jobs/coventry" element={<CoventryJobs />} />
        <Route path="/jobs/west-midlands" element={<WestMidlandsJobs />} />
        <Route path="/jobs/kent" element={<KentJobs />} />
        <Route path="/jobs/manchester" element={<ManchesterJobs />} />
        <Route path="/jobs/yorkshire" element={<YorkshireJobs />} />
        <Route path="/jobs/sussex" element={<SussexJobs />} />
        <Route path="/jobs/bristol" element={<BristolJobs />} />
        <Route path="/jobs/worcestershire" element={<WorcestershireJobs />} />
        <Route path="/jobs/dorset" element={<DorsetJobs />} />
        <Route path="/jobs/oxfordshire" element={<OxfordshireJobs />} />
        <Route path="/jobs/merseyside" element={<MerseysideJobs />} />
        <Route path="/jobs/doncaster" element={<DoncasterJobs />} />
        <Route path="/jobs/hertfordshire" element={<HertfordshireJobs />} />
        <Route path="/jobs/cornwall" element={<CornwallJobs />} />
        <Route path="/jobs/somerset" element={<SomersetJobs />} />
        <Route path="/jobs/bridgwater-with-ta6" element={<BridgwaterJobs />} />
        <Route path="/jobs/camborne-with-tr14" element={<CamborneJobs />} />
        <Route path="/jobs/shropshire" element={<ShropshireJobs />} />
        <Route path="/jobs/hampshire" element={<HampshireJobs />} />
        <Route path="/jobs/staffordshire" element={<StaffordshireJobs />} />
        <Route path="/jobs/buckinghamshire" element={<BuckinghamshireJobs />} />
        <Route path="/jobs/isle-of-wight" element={<IsleOfWightJobs />} />
        <Route path="/jobs/south-wales" element={<SouthWalesJobs />} />
        <Route path="/jobs/surrey" element={<SurreyJobs />} />
        {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  </TooltipProvider>
);

export default App;
